/**
 * Search page — URL-driven state (every filter lives in the search params so
 * results are shareable/bookmarkable):
 *
 *   ?query=…          free-text name search (input debounced 300ms → URL)
 *   ?segment=…        'brands' | 'shops' | 'promotions'   (default 'brands')
 *   ?categoryId=…     active category filter (+ categoryPath, categoryTitle)
 *   ?sort=…           'popular' | 'newest' | 'name' | 'subscribers'
 *
 * Only the active segment's query runs: each segment renders its own child
 * component so exactly one of useFilteredBrands/Shops/Promotions mounts at a
 * time (the data hooks have no `skip` support, so conditional mounting is the
 * way to fetch the active segment only).
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import {
  ArrowDownAZ,
  Building2,
  Clock,
  Flame,
  LayoutGrid,
  Search,
  Store,
  Tag,
  Users,
  X,
} from 'lucide-react';
import { Chip, EmptyState, ErrorState, Input, ListSkeleton } from '@/components/ui';
import { BrandCard, PromotionCard, ShopCard } from '@/components/cards';
import { CategoryPicker } from '@/components/category/CategoryPicker';
import {
  useFilteredBrands,
  useFilteredPromotions,
  useFilteredShops,
} from '@/hooks/useAppData';
import { buildCategoryWhere, buildSort, type SortKey } from '@/hooks/useCategories';
import { SEARCH_PAGE_DESCRIPTION, SEARCH_PAGE_TITLE } from '@/routes/legacyRoutes';
import { PageHead } from '@/components/seo/PageHead';
import { useImpressionRef } from '@/hooks/useImpressionTracking';
import { useCatalogPath, useRouteLocale } from '@/hooks/useRouteLocale';
import { categoryHubPath } from '@/seo/categoryHubs';
import { SEARCH_SETTLE_MS, trackSearch } from '@/lib/analytics';
import { iconForCategory } from '@/utils/categoryIcons';
import type { Category } from '@/types';

type SearchSegment = 'brands' | 'shops' | 'promotions';

const SEGMENTS: { key: SearchSegment; label: string; icon: typeof Building2 }[] = [
  { key: 'brands', label: 'Brands', icon: Building2 },
  { key: 'shops', label: 'Shops', icon: Store },
  { key: 'promotions', label: 'Promotions', icon: Tag },
];

const SORT_OPTIONS: { key: SortKey; label: string; icon: typeof Flame }[] = [
  { key: 'popular', label: 'Popular', icon: Flame },
  { key: 'newest', label: 'Newest', icon: Clock },
  { key: 'name', label: 'A → Z', icon: ArrowDownAZ },
  { key: 'subscribers', label: 'Most followed', icon: Users },
];

const SORT_KEYS: readonly SortKey[] = ['popular', 'newest', 'name', 'subscribers'];

const GRID_CLASS = 'grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';

const QUERY_DEBOUNCE_MS = 300;
/**
 * How long ?query= must stay unchanged before it is reported as a search —
 * the same wait as /search's page_view (usePageViews).
 */
const SEARCH_EVENT_DELAY_MS = SEARCH_SETTLE_MS;
const RESULT_LIMIT = 30;

// ─── Results plumbing (one child per segment so only the active query runs) ─

/** Grid cell wrapper that registers the card for impression view-tracking. */
function TrackedCell({
  entity,
  id,
  children,
}: {
  entity: 'brand' | 'shop' | 'promotion';
  id: string;
  children: ReactNode;
}) {
  const impressionRef = useImpressionRef(entity, id);
  // The cell is the grid item and stretches to its row; `*:h-full` passes that
  // height on to the card, so every card in a row is as tall as the tallest.
  return (
    <div ref={impressionRef} className="h-full *:h-full">
      {children}
    </div>
  );
}

interface ResultsFrameProps {
  loading: boolean;
  error: string | null;
  count: number;
  total: number | null;
  emptyMessage: string;
  showCount: boolean;
  onRetry: () => void;
  children: ReactNode;
}

function ResultsFrame({
  loading,
  error,
  count,
  total,
  emptyMessage,
  showCount,
  onRetry,
  children,
}: ResultsFrameProps) {
  if (loading && count === 0) return <ListSkeleton count={10} />;
  if (error && count === 0) return <ErrorState message={error} onRetry={onRetry} />;
  if (count === 0) return <EmptyState title="No results" message={emptyMessage} />;

  return (
    <div
      aria-busy={loading || undefined}
      className={`transition-opacity duration-200 ${loading ? 'opacity-60' : ''}`}
    >
      {showCount && total !== null && (
        <p className="mb-3 text-sm text-ink-secondary" aria-live="polite">
          {total} {total === 1 ? 'result' : 'results'}
        </p>
      )}
      {children}
    </div>
  );
}

interface SegmentResultsProps {
  where?: string;
  sort: string;
  emptyMessage: string;
  showCount: boolean;
}

function BrandResults({ where, sort, emptyMessage, showCount }: SegmentResultsProps) {
  const { items, loading, error, pagination, refetch } = useFilteredBrands({
    where,
    sort,
    limit: RESULT_LIMIT,
  });
  return (
    <ResultsFrame
      loading={loading}
      error={error}
      count={items.length}
      total={pagination?.totalRecords ?? null}
      emptyMessage={emptyMessage}
      showCount={showCount}
      onRetry={refetch}
    >
      <div className={GRID_CLASS}>
        {items.map((brand) => (
          <TrackedCell key={brand.id} entity="brand" id={brand.id}>
            <BrandCard brand={brand} />
          </TrackedCell>
        ))}
      </div>
    </ResultsFrame>
  );
}

function ShopResults({ where, sort, emptyMessage, showCount }: SegmentResultsProps) {
  const { items, loading, error, pagination, refetch } = useFilteredShops({
    where,
    sort,
    limit: RESULT_LIMIT,
  });
  return (
    <ResultsFrame
      loading={loading}
      error={error}
      count={items.length}
      total={pagination?.totalRecords ?? null}
      emptyMessage={emptyMessage}
      showCount={showCount}
      onRetry={refetch}
    >
      <div className={GRID_CLASS}>
        {items.map((shop) => (
          <TrackedCell key={shop.id} entity="shop" id={shop.id}>
            <ShopCard shop={shop} />
          </TrackedCell>
        ))}
      </div>
    </ResultsFrame>
  );
}

function PromotionResults({ where, sort, emptyMessage, showCount }: SegmentResultsProps) {
  const { items, loading, error, pagination, refetch } = useFilteredPromotions({
    where,
    sort,
    limit: RESULT_LIMIT,
  });
  return (
    <ResultsFrame
      loading={loading}
      error={error}
      count={items.length}
      total={pagination?.totalRecords ?? null}
      emptyMessage={emptyMessage}
      showCount={showCount}
      onRetry={refetch}
    >
      <div className={GRID_CLASS}>
        {items.map((promotion) => (
          <TrackedCell key={promotion.id} entity="promotion" id={promotion.id}>
            <PromotionCard promotion={promotion} variant="grid" />
          </TrackedCell>
        ))}
      </div>
    </ResultsFrame>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

/**
 * The old category link `/search?categoryId=…&categoryPath=…&categoryTitle=…`
 * lands on the category's hub (`/{cc}/{lang}/category/…`), the one canonical
 * URL, with one client `replace`. Only a link that opens on a bare category
 * (no query, not the shops segment, which the hub does not list) moves; the
 * decision is made once on arrival, so picking a category inside the search
 * page keeps the search page.
 */
export default function SearchPage() {
  const [searchParams] = useSearchParams();
  const locale = useRouteLocale();
  const [hub] = useState(() =>
    searchParams.get('query')?.trim() || searchParams.get('segment') === 'shops'
      ? null
      : categoryHubPath({ path: searchParams.get('categoryPath') ?? undefined }, locale),
  );
  if (hub) return <Navigate replace to={hub} />;
  return <SearchResultsPage />;
}

function SearchResultsPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL is the single source of truth for every filter.
  const urlQuery = searchParams.get('query') ?? '';
  const segmentParam = searchParams.get('segment');
  const segment: SearchSegment =
    segmentParam === 'shops' || segmentParam === 'promotions' ? segmentParam : 'brands';
  const sortParam = searchParams.get('sort') as SortKey | null;
  const sort: SortKey = sortParam && SORT_KEYS.includes(sortParam) ? sortParam : 'popular';
  const categoryId = searchParams.get('categoryId');
  const categoryPath = searchParams.get('categoryPath');
  const categoryTitle = searchParams.get('categoryTitle');
  const hasCategory = Boolean(categoryId || categoryPath || categoryTitle);

  // `noindex, follow` with or without a query (master D-5, web plan W0.3).
  // robots.txt disallows only `/search?`, never the bare path, so crawlers can
  // still fetch /search and see this.
  // `/pk/en/search` once the URL move is live (web plan W2.1); robots.txt then
  // also disallows `/*/*/search?`.
  // A category browse names the category's hub as its canonical URL, so it is
  // never a duplicate of it; the page itself stays noindex.
  const locale = useRouteLocale();
  const catalogSearch = useCatalogPath()('/search');
  const canonicalPath = categoryHubPath({ path: categoryPath ?? undefined }, locale) ?? catalogSearch;

  const [input, setInput] = useState(urlQuery);
  const [pickerOpen, setPickerOpen] = useState(false);

  const updateParams = useCallback(
    (updates: Record<string, string | null>, opts?: { replace?: boolean }) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: opts?.replace ?? false },
      );
    },
    [setSearchParams],
  );

  // Debounce typed text into ?query= (replace, so typing doesn't spam history).
  useEffect(() => {
    if (input === urlQuery) return undefined;
    const timer = window.setTimeout(() => {
      updateParams({ query: input }, { replace: true });
    }, QUERY_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [input, urlQuery, updateParams]);

  // External URL changes (header search, back/forward) re-hydrate the input.
  useEffect(() => {
    setInput(urlQuery);
  }, [urlQuery]);

  // GA4 `search` once the query has settled. Typing rewrites ?query= every
  // QUERY_DEBOUNCE_MS, so reporting each URL change would count every prefix
  // ("n", "ni", "nik"…) as a search of its own.
  useEffect(() => {
    if (!urlQuery.trim()) return undefined;
    const timer = window.setTimeout(() => trackSearch(urlQuery), SEARCH_EVENT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [urlQuery]);

  const where = useMemo(
    () =>
      buildCategoryWhere({
        query: urlQuery,
        categoryPath,
        // Path filtering subsumes the id filter; only fall back to the id
        // when no path is available (mirrors the mobile screen).
        categoryId: categoryPath ? null : categoryId,
      }),
    [urlQuery, categoryPath, categoryId],
  );

  const sortString = useMemo(() => buildSort(sort), [sort]);

  const emptyMessage = urlQuery
    ? `Nothing found for "${urlQuery}"${categoryTitle ? ` in ${categoryTitle}` : ''}`
    : categoryTitle
      ? `No ${segment} in ${categoryTitle}`
      : 'Start typing or pick a category.';

  const clearQuery = () => {
    setInput('');
    updateParams({ query: null }, { replace: true });
  };

  const clearCategory = () => {
    updateParams({ categoryId: null, categoryPath: null, categoryTitle: null });
  };

  const handlePickerSelect = (category: Category | null) => {
    if (category) {
      updateParams({
        categoryId: category.id,
        categoryPath: category.path ?? null,
        categoryTitle: category.title,
      });
    } else {
      clearCategory();
    }
    setPickerOpen(false);
  };

  const ActiveCategoryIcon = iconForCategory(categoryTitle);

  const resultsProps: SegmentResultsProps = {
    where,
    sort: sortString,
    emptyMessage,
    showCount: Boolean(where),
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 md:py-8">
      <PageHead
        title={urlQuery ? `Search "${urlQuery}"` : SEARCH_PAGE_TITLE}
        description={SEARCH_PAGE_DESCRIPTION}
        canonicalPath={canonicalPath}
        noindex
      />
      <h1 className="text-2xl font-extrabold tracking-tight text-ink">Search</h1>

      {/* Search input */}
      <div className="mt-4">
        <Input
          type="text"
          enterKeyHint="search"
          // Don't steal focus (or pop the mobile keyboard) when arriving via a
          // category chip to browse pre-filtered results.
          autoFocus={!categoryId && !categoryPath}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Search brands, shops, promotions..."
          aria-label="Search brands, shops, promotions"
          leftIcon={<Search size={20} aria-hidden="true" />}
          rightIcon={
            input.length > 0 ? (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearQuery}
                className="flex h-6 w-6 items-center justify-center rounded-full text-ink-tertiary transition-all duration-200 hover:bg-line-light hover:text-ink"
              >
                <X size={16} />
              </button>
            ) : undefined
          }
        />
      </div>

      {/* Segment pills */}
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Result type">
        {SEGMENTS.map(({ key, label, icon: Icon }) => {
          const active = segment === key;
          return (
            <button
              key={key}
              type="button"
              aria-pressed={active}
              onClick={() => updateParams({ segment: key })}
              className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-semibold transition-all duration-200 ${
                active
                  ? 'border-primary bg-primary text-white'
                  : 'border-line bg-surface text-ink-secondary hover:border-primary/40'
              }`}
            >
              <Icon size={15} aria-hidden="true" />
              {label}
            </button>
          );
        })}
      </div>

      {/* Filter row: category control + sort pills */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {hasCategory && categoryTitle ? (
          <Chip
            label={categoryTitle}
            color="primary"
            icon={<ActiveCategoryIcon size={14} aria-hidden="true" />}
            onClick={() => setPickerOpen(true)}
            onRemove={clearCategory}
            className="max-w-[240px]"
          />
        ) : (
          <Chip
            label="All categories"
            color="neutral"
            outline
            icon={<LayoutGrid size={14} aria-hidden="true" />}
            onClick={() => setPickerOpen(true)}
          />
        )}

        <div
          className="flex min-w-0 flex-1 items-center gap-1.5 overflow-x-auto"
          role="group"
          aria-label="Sort results"
        >
          {SORT_OPTIONS.map(({ key, label, icon: Icon }) => {
            const active = sort === key;
            return (
              <button
                key={key}
                type="button"
                aria-pressed={active}
                onClick={() => updateParams({ sort: key })}
                className={`inline-flex shrink-0 items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-all duration-200 ${
                  active
                    ? 'bg-ink text-background'
                    : 'bg-line-light text-ink-secondary hover:bg-line'
                }`}
              >
                <Icon size={12} aria-hidden="true" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Results — only the active segment's component mounts, so only its
          query runs. cache-and-network keeps revisits instant. */}
      <div className="mt-6">
        {segment === 'brands' && <BrandResults {...resultsProps} />}
        {segment === 'shops' && <ShopResults {...resultsProps} />}
        {segment === 'promotions' && <PromotionResults {...resultsProps} />}
      </div>

      <CategoryPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handlePickerSelect}
        initial={
          hasCategory
            ? {
                id: categoryId ?? '',
                title: categoryTitle ?? '',
                path: categoryPath ?? undefined,
              }
            : null
        }
      />
    </div>
  );
}
